import { createSDK } from '@adsk/bim360-rfis-sdk';
import Oxygen from '../oxygen';
import DataNormalize from './helper/dataNormalize';
import { ATTACHMENT_SORT, getSdkEnv } from './helper/config';
import buildGetRfisRequest from './helper/getRfis-helper';
import { getToken } from '../../utils/getToken';
import { tokenExpired } from '../../redux/actions';

const getSdkConfig = ({ config, getState, dispatch }) => {
  const { env, region, containerId } = config;
  const sdkEnv = getSdkEnv(env);

  const addTokenToRequest = (url, fetchData = {}) =>
    getToken({ getState, dispatch }).then((token) => {
      if (!fetchData.headers) {
        fetchData.headers = {};
      }
      fetchData.headers['Content-Type'] = 'application/json';
      fetchData.headers.Authorization = `Bearer ${token}`;
      return { url, fetchData };
    });
  const dispatchTokenExpired = (response = {}) => {
    if (response.status === 401) {
      dispatch(tokenExpired());
    }
    return Promise.resolve(response);
  };

  const fetchOptions = {
    getExtendedRequest: addTokenToRequest,
    onError: dispatchTokenExpired,
  };

  const sdkConfig = { containerId, env: sdkEnv, region, fetchOptions };
  return sdkConfig;
};

export default class {
  constructor(dispatch, getState, config) {
    this.dispatch = dispatch;
    this.getState = getState;
    this.oxygen = new Oxygen();
    this.dataNormalize = new DataNormalize({ oxygen: this.oxygen });

    const sdkConfig = getSdkConfig({ config, getState, dispatch });
    this.sdk = createSDK(sdkConfig);
  }

  configureOxygenMappings(oxygenMappings) {
    this.oxygen.setMappings(oxygenMappings);
  }

  // TODO: delete while removing api.v2
  // eslint-disable-next-line class-methods-use-this
  setConfig() {}

  // Implemented by RFI's SDK
  getRfiById(rfiId) {
    return this.sdk.RFIs.get(rfiId).then((data) => this.dataNormalize.deserializeRfi(data));
  }

  getRfis(query) {
    const request = buildGetRfisRequest(query);
    return this.sdk.RFIs.getAll(request).then((data) => this.dataNormalize.deserializeRfis(data));
  }

  createRfi(rfiAttributes) {
    const serializedRfi = this.dataNormalize.serializeRfi(rfiAttributes);
    return this.sdk.RFIs.create(serializedRfi).then((createdRfi) =>
      this.dataNormalize.deserializeRfi(createdRfi)
    );
  }

  updateRfi({ id }, fieldsToUpdate) {
    const serializedRfi = this.dataNormalize.serializeRfi(fieldsToUpdate);
    return this.sdk.RFIs.update(id, serializedRfi).then((updatedRfi) =>
      this.dataNormalize.deserializeRfi(updatedRfi)
    );
  }

  // customIdentifier
  getRfiCustomIdentifier() {
    return this.sdk.RFIs.customIdentifier
      .getSuggested()
      .then(({ next }) => ({ suggestedNextCustomIdentifier: next }));
  }

  validateRfiCustomIdentifier(customIdentifier) {
    return this.sdk.RFIs.customIdentifier
      .validate(customIdentifier)
      .then((isValid) => ({ isValid, isTaken: !isValid }))
      .catch((err) => err);
  }

  getRfiCustomIdentifierWithRevision(rfiId) {
    return this.sdk.RFIs.customIdentifier.getRevision(rfiId).then(({ next }) => {
      return { suggestedNextCustomIdentifier: next };
    });
  }

  // users
  getCurrentUser() {
    return this.sdk.Users.me().then((user) => {
      return this.dataNormalize.deserializeUser(user);
    });
  }

  // comments
  getComments(rfiId) {
    return this.sdk.RFIs.getComments({ rfiId, limit: 200 }).then((response) => {
      return this.dataNormalize.deserializeComments(response);
    });
  }

  createComment(rfiId, body) {
    return this.sdk.RFIs.createComment(rfiId, body).then((response) =>
      this.dataNormalize.deserializeComment(response)
    );
  }

  // attachments
  getAttachments(rfiId) {
    return this.sdk.RFIs.getAttachments({
      rfiId,
      limit: 200,
      sort: ATTACHMENT_SORT,
      filter: {
        includeDeleted: true,
      },
    }).then((getAttachmentsResponse) => {
      return this.dataNormalize.deserializeAttachments(getAttachmentsResponse);
    });
  }

  createAttachment(rfiId, attributes) {
    return this.sdk.RFIs.createAttachment(rfiId, attributes).then((createdAttachmentResponse) =>
      this.dataNormalize.deserializeAttachment(createdAttachmentResponse)
    );
  }

  deleteAttachment({ issueId: rfiId, attachmentId }) {
    return this.sdk.RFIs.deleteAttachment(rfiId, attachmentId);
  }

  // changesets
  getChangesets(rfiId) {
    return this.sdk.RFIs.getChangesets({
      rfiId,
      limit: 200,
    }).then((response) => {
      return this.dataNormalize.deserializeChangesets(response);
    });
  }

  // channels
  getChannelItems(issueId) {
    const getActivities = [
      this.getAttachments(issueId),
      this.getChangesets(issueId),
      this.getComments(issueId),
    ];

    return Promise.all(getActivities).then((results) => {
      const flatResults = results.reduce((acc, val) => acc.concat(val), []);
      return flatResults;
    });
  }

  // links
  createLinks(rfiId, items) {
    return this.sdk.RFIs.createLinks(rfiId, { items });
  }

  deleteLink(rfiId, item) {
    const items = [item];
    return this.sdk.RFIs.removeLinks(rfiId, { items }).then(() => ({ items }));
  }

  // workflows
  getWorkflows() {
    return this.sdk.Workflow.get().then(this.dataNormalize.deserializeWorkflows);
  }

  createWorkflow(workflowType) {
    return this.sdk.Workflow.update({ workflowType });
  }

  updateState(id, attributes) {
    const serializedProjectRolesMapping = this.dataNormalize.serializeProjectRolesMapping(
      id,
      attributes
    );
    return this.sdk.Workflow.update(serializedProjectRolesMapping).then(
      this.dataNormalize.deserializeWorkflows
    );
  }

  // virtual folders

  createVirtualFolder() {
    return this.sdk.VirtualFolders.create();
  }
}
